import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { CUPET_STATUS_LABEL, CupetAllocationStatus, type CupetAllocationSummary } from '@tucupet/shared';
import { PageHeader, Card, Btn } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { ConsumptionBar, StatusBadge } from './cupet-shared';

interface HistoryEntry {
  status: number;
  realAmount: number | null;
  source: string;
  at: string;
  note: string | null;
}
type Detail = CupetAllocationSummary & {
  phoneProvider: string;
  mailProvider: string | null;
  pin: string | null;
  history: HistoryEntry[];
};

export default function CupetDetailPage() {
  const { externalId = '' } = useParams();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'cupet', 'detail', externalId] });
    qc.invalidateQueries({ queryKey: ['admin', 'cupet', 'list'] });
  };

  const { data, isLoading } = useQuery<Detail>({
    queryKey: ['admin', 'cupet', 'detail', externalId],
    queryFn: () => api(`/api/admin/cupet/allocations/${externalId}`),
  });

  const sync = useMutation({
    mutationFn: () => api(`/api/admin/cupet/allocations/${externalId}/sync`, { body: {} }),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/admin/cupet/allocations/${externalId}/cancel`, { body: {} }),
    onSuccess: invalidate,
  });

  if (isLoading || !data) {
    return (
      <div>
        <Link to="/cupet" className="mono" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>← Asignaciones</Link>
        <p style={{ color: 'var(--text-3)', marginTop: 24 }}>Cargando…</p>
      </div>
    );
  }

  const cancellable = data.status !== CupetAllocationStatus.CANCELLED;
  const actionErr = (m: typeof sync | typeof cancel) =>
    m.isError ? ((m.error as ApiError)?.message ?? 'Error') : null;

  return (
    <div>
      <Link to="/cupet" className="mono" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>← Asignaciones</Link>
      <div style={{ height: 12 }} />
      <PageHeader
        kicker="ASIGNACIÓN CUPET"
        title={data.externalId}
        sub={`Transacción CUPET ${data.transactionId ?? '—'}`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}>{sync.isPending ? 'Sincronizando…' : 'Sincronizar'}</Btn>
            {cancellable && <Btn variant="danger" size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>{cancel.isPending ? 'Cancelando…' : 'Cancelar'}</Btn>}
          </div>
        }
      />

      <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: 'color-mix(in oklab, oklch(0.78 0.16 70) 10%, transparent)', border: '1px solid color-mix(in oklab, oklch(0.78 0.16 70) 35%, transparent)', fontSize: 12, color: 'var(--text-2)' }}>
        ⚠ <b>Sincronizar</b> y <b>Cancelar</b> dependen de endpoints de CUPET (GetOrder / CancelOrder) que el proveedor aún está corrigiendo. Hasta entonces pueden devolver error; la asignación local se mantiene.
      </div>

      {(actionErr(sync) || actionErr(cancel)) && (
        <div style={{ marginBottom: 16, color: 'var(--danger)', fontSize: 12 }}>{actionErr(sync) || actionErr(cancel)}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16, alignItems: 'start' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em' }}>CONSUMO</div>
            <StatusBadge status={data.status} />
          </div>
          <ConsumptionBar amount={data.amount} realAmount={data.realAmount} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginTop: 20 }}>
            <Metric label="Asignado" value={`${data.amount} L`} />
            <Metric label="Restante" value={`${data.realAmount} L`} accent />
            <Metric label="Consumido" value={`${data.consumed} L`} />
          </div>
          {data.refundAmount != null && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-2)' }}>Reembolso por cancelación: <b>{data.refundAmount} L</b></div>}
          <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-3)' }}>
            {data.expirationDate && <>Vence: {new Date(data.expirationDate).toLocaleString('es-MX')} · </>}
            {data.lastSyncedAt ? <>Última sync: {new Date(data.lastSyncedAt).toLocaleString('es-MX')}</> : 'Sin sincronizar aún'}
          </div>
        </Card>

        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 12 }}>CLIENTE</div>
          <Row label="Nombre" value={data.nameProvider || '—'} />
          <Row label="NIT / CI" value={data.identifyProvider} mono />
          <Row label="Teléfono" value={data.phoneProvider || '—'} />
          <Row label="Email" value={data.mailProvider || '—'} />
          <Row label="PIN" value={data.pin || '—'} mono />
          <Row label="Combustible" value={`#${data.fuelTypeId}`} mono />
          <Row label="Servicentro" value={`#${data.servicenterId}`} mono />
          {data.userId && <Row label="Usuario" value={data.userId} mono />}
        </Card>
      </div>

      <Card style={{ marginTop: 16 }}>
        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 16 }}>HISTORIAL</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {data.history.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Sin eventos.</div>}
          {[...data.history].reverse().map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
              <div style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--accent)', marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>
                  {CUPET_STATUS_LABEL[h.status] ?? h.status}
                  {h.realAmount != null && <span style={{ color: 'var(--text-3)' }}> · {h.realAmount} L restantes</span>}
                </div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  {new Date(h.at).toLocaleString('es-MX')} · {sourceLabel(h.source)}{h.note ? ` · ${h.note}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function sourceLabel(s: string) {
  return s === 'webhook' ? 'notificación CUPET' : s === 'sync' ? 'sincronización' : 'plataforma';
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em' }}>{label.toUpperCase()}</div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: accent ? 'var(--accent)' : 'var(--text)' }}>{value}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</span>
      <span className={mono ? 'mono' : undefined} style={{ fontSize: 13, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
